import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import useLoad from '../utils/useLoad';
import { useOrgStore } from '../store/org';
import { vpApi, errorMessage, isUnavailable, fmtDateTime } from '../api/velopulse';

const STEPS = ['Channel 憑證', 'Webhook', '訊息與選單', '測試與啟用'];

// 失敗時給可執行的修復提示，而不是堆疊訊息
function validateCredentials({ channel_id, channel_secret }, hasSecret) {
  if (!/^\d{8,12}$/.test(channel_id.trim())) return 'Channel ID 應為 8–12 位數字，請到 LINE Developers 的「Basic settings」複製。';
  if (!(hasSecret && !channel_secret) && !/^[0-9a-f]{32}$/i.test(channel_secret.trim())) return 'Channel Secret 應為 32 個英數字元，請到「Basic settings」重新複製，注意不要含空白。';
  return '';
}

function StepCredentials({ wizard, onSaved }) {
  const cred = wizard.credentials || {};
  const [form, setForm] = useState({ channel_id: cred.channel_id || '', channel_secret: '', access_token: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (cred.managed_by_env) {
    return (
      <>
        <h2>步驟 1：Channel 憑證</h2>
        <p>這個環境的 LINE Channel 憑證由系統管理員透過伺服器設定管理，無法在此修改。</p>
        {cred.has_secret && cred.has_access_token
          ? <div className="vp-okbox">✓ 憑證已設定，可以繼續下一步</div>
          : <div className="vp-warnbox">伺服器尚未設定完整的 LINE 憑證，請聯絡系統管理員補上後再繼續。</div>}
        <div className="vp-wiz-actions"><span /><button type="button" className="vp-btn lg primary" disabled={!(cred.has_secret && cred.has_access_token)} onClick={() => onSaved(null)}>下一步</button></div>
      </>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const msg = validateCredentials(form, cred.has_secret);
    if (msg) { setError(msg); return; }
    setSaving(true); setError('');
    try {
      onSaved(await vpApi.saveLineWizard(1, { channel_id: form.channel_id.trim(), channel_secret: form.channel_secret.trim() || undefined, access_token: form.access_token.trim() || undefined }));
    } catch (err) { setError(errorMessage(err, '憑證驗證未通過，請確認後再試。')); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit}>
      <h2>步驟 1：建立／選擇 Channel</h2>
      <p>請到 LINE Developers 建立 Messaging API Channel，然後把憑證貼到下方。Secret 與 Token 儲存後不會再顯示。</p>
      <div className="vp-row2">
        <div><label className="vp-label" htmlFor="lw-cid">Channel ID</label><input id="lw-cid" className="vp-input" inputMode="numeric" value={form.channel_id} onChange={set('channel_id')} autoComplete="off" /></div>
        <div><label className="vp-label" htmlFor="lw-sec">Channel Secret</label><input id="lw-sec" className="vp-input" type="password" value={form.channel_secret} onChange={set('channel_secret')} placeholder={cred.has_secret ? '已設定（留白＝不變更）' : ''} autoComplete="off" /></div>
      </div>
      <label className="vp-label" htmlFor="lw-tok">Channel Access Token（選填）</label>
      <input id="lw-tok" className="vp-input" type="password" value={form.access_token} onChange={set('access_token')} placeholder={cred.has_access_token ? '已設定（留白＝不變更）' : ''} autoComplete="off" />
      {error && <div className="vp-error" role="alert">{error}</div>}
      <div className="vp-wiz-actions"><span /><button type="submit" className="vp-btn lg primary" disabled={saving}>{saving ? '驗證中…' : '驗證並繼續'}</button></div>
    </form>
  );
}

function StepWebhook({ wizard, onBack, onSaved }) {
  const url = wizard.webhook_url || `${window.location.origin}/api/linebot/webhook`;
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState('');
  const verified = !!wizard.webhook?.verified;

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('已複製 Webhook URL'); } catch { toast.error('無法自動複製，請手動選取網址複製。'); }
  };
  const probe = async () => {
    setProbing(true); setError('');
    try {
      const r = await vpApi.probeLineWebhook();
      if (r?.ok) onSaved(await vpApi.saveLineWizard(2, { manual: false }));
      else setError('尚未偵測到連線。請確認已在 LINE Console 貼上網址並開啟「Use webhook」，再按一次「偵測連線」。');
    } catch (err) { setError(errorMessage(err, '偵測失敗。請確認 LINE Console 的 Webhook 已開啟後再試。')); } finally { setProbing(false); }
  };
  const manual = async () => {
    setProbing(true); setError('');
    try { onSaved(await vpApi.saveLineWizard(2, { manual: true })); } catch (err) { setError(errorMessage(err)); } finally { setProbing(false); }
  };

  return (
    <>
      <h2>步驟 2：設定 Webhook</h2>
      <p>複製下方網址，到 LINE Developers Console 的「Messaging API」分頁貼到 Webhook URL，並開啟「Use webhook」。</p>
      <div className="vp-wh"><span>{url}</span><button type="button" onClick={copy}>複製</button></div>
      {verified && <div className="vp-okbox">✓ Webhook 已確認{wizard.webhook.verified_at ? `（${fmtDateTime(wizard.webhook.verified_at)}）` : ''}</div>}
      {error && <div className="vp-error" role="alert">{error}</div>}
      <div className="vp-wiz-actions">
        <button type="button" className="vp-btn lg" onClick={onBack}>上一步</button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" className="vp-btn lg" onClick={manual} disabled={probing}>我已設定</button>
          <button type="button" className="vp-btn lg primary" onClick={probe} disabled={probing}>{probing ? '偵測中…' : '偵測連線'}</button>
        </div>
      </div>
    </>
  );
}

function StepMessages({ wizard, orgName, onBack, onSaved }) {
  const init = wizard.messages || {};
  const [welcome, setWelcome] = useState(init.welcome || '');
  const [menu, setMenu] = useState([0, 1, 2, 3].map((i) => init.menu?.[i] || ''));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!welcome.trim()) { setError('請填寫歡迎詞。'); return; }
    if (!menu.some((m) => m.trim())) { setError('請至少填寫一個選單項目。'); return; }
    setSaving(true); setError('');
    try { onSaved(await vpApi.saveLineWizard(3, { welcome: welcome.trim(), menu: menu.map((m) => m.trim()).filter(Boolean) })); } catch (err) { setError(errorMessage(err, '儲存失敗，請稍後再試。')); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit}>
      <h2>步驟 3：歡迎詞與主選單</h2>
      <p>顧客第一次加入或輸入「選單」時會看到這些內容。請使用繁體中文、口吻與門市一致。</p>
      <label className="vp-label" htmlFor="lw-welcome">歡迎詞</label>
      <textarea id="lw-welcome" className="vp-input" value={welcome} onChange={(e) => setWelcome(e.target.value)} />
      <div className="vp-row2">
        {menu.map((m, i) => (
          <div key={i}><label className="vp-label" htmlFor={`lw-m${i}`}>選單項目 {i + 1}</label><input id={`lw-m${i}`} className="vp-input" value={m} maxLength={20} onChange={(e) => setMenu((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} /></div>
        ))}
      </div>
      <div className="vp-label">手機預覽</div>
      <div className="vp-preview">
        {orgName}官方帳號
        <div className="vp-pbubble">
          {welcome || '（尚未填寫歡迎詞）'}
          {menu.some((m) => m.trim()) && <div className="vp-pmenu">{menu.filter((m) => m.trim()).map((m, i) => <span key={i}>{m}</span>)}</div>}
        </div>
      </div>
      {error && <div className="vp-error" role="alert">{error}</div>}
      <div className="vp-wiz-actions">
        <button type="button" className="vp-btn lg" onClick={onBack}>上一步</button>
        <button type="submit" className="vp-btn lg primary" disabled={saving}>{saving ? '儲存中…' : '儲存並前往測試'}</button>
      </div>
    </form>
  );
}

function StepTest({ wizard, onBack, onSaved }) {
  const navigate = useNavigate();
  const [lastEventAt, setLastEventAt] = useState(wizard.test?.last_event_at || null);
  const [error, setError] = useState('');
  const [enabling, setEnabling] = useState(false);

  // 等待測試事件：每 3 秒詢問一次，收到就停止
  useEffect(() => {
    if (lastEventAt || wizard.enabled) return undefined;
    let stop = false;
    const tick = async () => {
      try { const r = await vpApi.getLineTestStatus(); if (!stop && r?.last_event_at) setLastEventAt(r.last_event_at); } catch (err) { if (!stop) setError(errorMessage(err, '無法取得測試狀態。')); }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [lastEventAt, wizard.enabled]);

  const enable = async () => {
    setEnabling(true); setError('');
    try { onSaved(await vpApi.enableLineBot()); toast.success('LINE Bot 已啟用'); } catch (err) { setError(errorMessage(err, '啟用失敗，請稍後再試。')); } finally { setEnabling(false); }
  };

  if (wizard.enabled) {
    return (
      <>
        <h2>設定完成</h2>
        <p>LINE Bot 已啟用，顧客傳來的訊息會進入「訊息」。</p>
        <div className="vp-okbox">✓ Bot 已啟用</div>
        <div className="vp-wiz-actions"><button type="button" className="vp-btn lg" onClick={onBack}>上一步</button><button type="button" className="vp-btn lg primary" onClick={() => navigate('/linebot')}>前往訊息</button></div>
      </>
    );
  }

  return (
    <>
      <h2>步驟 4：測試與啟用</h2>
      <p>請用手機加入官方帳號，傳送任何一段測試文字。系統收到事件後，就可以啟用 Bot。</p>
      {lastEventAt
        ? <div className="vp-okbox">✓ 已收到測試事件（{fmtDateTime(lastEventAt)}）· 可以啟用</div>
        : <div className="vp-warnbox" role="status">等待測試訊息中…若超過一分鐘沒有反應，請回步驟 2 確認 Webhook 已開啟。</div>}
      {error && <div className="vp-error" role="alert">{error}</div>}
      <div className="vp-wiz-actions">
        <button type="button" className="vp-btn lg" onClick={onBack}>上一步</button>
        <button type="button" className="vp-btn lg primary" disabled={!lastEventAt || enabling} onClick={enable}>{enabling ? '啟用中…' : '啟用 Bot'}</button>
      </div>
    </>
  );
}

function LineBotWizard() {
  const navigate = useNavigate();
  const org = useOrgStore((s) => s.currentOrg);
  const { data: wizard, error, loading, reload, setData } = useLoad(() => vpApi.getLineWizard(), []);
  const [step, setStep] = useState(null);

  useEffect(() => { if (wizard && step === null) setStep(Math.min(Math.max(wizard.step || 1, 1), 4)); }, [wizard, step]);

  if (loading || (wizard && step === null)) return <VpState loading />;

  // 未設定／後端未就緒：fail-closed，不顯示假的可填表單
  if (error || wizard?.available === false) {
    return (
      <div className="vp-wiz">
        <h1>LINE Bot 設定精靈</h1>
        <p className="vp-lead">四個步驟完成連線。</p>
        <div className="vp-wiz-card">
          <VpState
            text={error && !isUnavailable(error) ? errorMessage(error) : '這個組織的 LINE Bot 設定服務尚未啟用，暫時無法在此完成設定。請聯絡系統管理員。'}
            action={error && !isUnavailable(error) ? { label: '重新載入', onClick: reload } : { label: '查看既有 LINE Bot 管理', onClick: () => navigate('/linebot') }}
          />
        </div>
      </div>
    );
  }

  const reached = wizard.enabled ? 4 : Math.max(wizard.step || 1, step);
  const saved = (next, goto) => { if (next) setData(next); setStep(goto); };

  return (
    <div className="vp-wiz">
      <h1>LINE Bot 設定精靈</h1>
      <p className="vp-lead">四個步驟完成連線，每一步都可以回上一頁修改。設定對象：{org?.name}。</p>
      <div className="vp-steps" role="list" aria-label="設定進度">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const cls = n === step ? ' on' : n < reached || wizard.enabled ? ' done' : '';
          return <div key={label} role="listitem" aria-current={n === step ? 'step' : undefined} className={`vp-step${cls}`}><span className="n">步驟 {n}{cls === ' done' ? ' ✓' : ''}</span>{label}</div>;
        })}
      </div>
      <div className="vp-wiz-card">
        {step === 1 && <StepCredentials wizard={wizard} onSaved={(w) => saved(w, 2)} />}
        {step === 2 && <StepWebhook wizard={wizard} onBack={() => setStep(1)} onSaved={(w) => saved(w, 3)} />}
        {step === 3 && <StepMessages wizard={wizard} orgName={org?.short_name || org?.name || ''} onBack={() => setStep(2)} onSaved={(w) => saved(w, 4)} />}
        {step === 4 && <StepTest wizard={wizard} onBack={() => setStep(3)} onSaved={(w) => saved(w, 4)} />}
      </div>
    </div>
  );
}

export default function LineBotWizardPage() {
  return <RequireOrg><LineBotWizard /></RequireOrg>;
}
